import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';

// Switch a FastTrak survey between its two azimuth datums.
//
// The tool re-runs a coarse alignment (north-seek) whenever it judges itself
// stationary enough, and each one replaces its heading datum. Northseek treats
// those alignments as the measurement and uses the reported heading exactly as
// recorded — the historical behaviour. Reference subtracts the azimuth change
// across every alignment (no real motion happens while the counter is
// incrementing) and anchors the survey to an operator-declared azimuth instead.
//
// Nothing about the raw orientation records changes, so this is reversible in
// both directions any number of times.
//
// A legacy StartAzimuth meant "heading fed to the tool at initialisation",
// which the tool discarded at its first alignment. It is a reasonable default
// to offer but was never validated as a tie-in, so the conversion is previewed
// and confirmed rather than applied silently.
export const AzimuthModeModal = ({ isOpen, surveyKey, survey, onClose, onSurveyUpdated }) => {
    const storedMode = (survey?.AzimuthMode || 'NORTHSEEK').toUpperCase() === 'REFERENCE'
        ? 'REFERENCE' : 'NORTHSEEK';
    const [mode, setMode] = useState(storedMode);
    const [referenceAzimuth, setReferenceAzimuth] = useState('');
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Start from whatever the survey is already on each time it is opened.
    useEffect(() => {
        if (!isOpen) return;
        setMode(storedMode);
        setReferenceAzimuth(String(survey?.StartAzimuth ?? 0));
        setError(null);
    }, [isOpen, surveyKey, storedMode, survey?.StartAzimuth]);

    const parsedAzimuth = parseFloat(referenceAzimuth);
    const azimuthIsValid = mode === 'NORTHSEEK' || Number.isFinite(parsedAzimuth);

    const loadPreview = useCallback(async () => {
        if (!isOpen || !surveyKey || !azimuthIsValid) { setPreview(null); return; }
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`api/surveys/${surveyKey}/azimuth/preview`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mode, referenceAzimuth: parsedAzimuth || 0 })
            });
            if (!response.ok) {
                setError(await response.text() || 'Could not work out what this change would do.');
                setPreview(null);
                return;
            }
            setPreview(await response.json());
        } catch (e) {
            setError('Could not work out what this change would do.');
            setPreview(null);
        } finally {
            setLoading(false);
        }
    }, [isOpen, surveyKey, mode, parsedAzimuth, azimuthIsValid]);

    useEffect(() => {
        // Debounced so typing an azimuth doesn't fire a request per keystroke.
        const timer = setTimeout(loadPreview, 300);
        return () => clearTimeout(timer);
    }, [loadPreview]);

    const apply = async () => {
        setSaving(true);
        setError(null);
        try {
            const response = await fetch(`api/surveys/${surveyKey}/azimuth`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mode, referenceAzimuth: parsedAzimuth || 0 })
            });
            if (!response.ok) {
                setError(await response.text() || 'Could not change the azimuth datum.');
                return;
            }
            const updated = await response.json();
            if (onSurveyUpdated) onSurveyUpdated(updated);
            onClose();
        } catch (e) {
            setError('Could not change the azimuth datum.');
        } finally {
            setSaving(false);
        }
    };

    const formatOffset = (offset) => `${offset >= 0 ? '+' : ''}${offset.toFixed(2)}°`;
    const changed = mode !== storedMode
        || (mode === 'REFERENCE' && parsedAzimuth !== (survey?.StartAzimuth ?? 0));

    // Wrapped to +/-180 so a datum either side of north reads as a small step
    // rather than a ~360 jump.
    const wrap180 = (delta) => {
        let d = delta;
        while (d > 180) d -= 360;
        while (d < -180) d += 360;
        return d;
    };

    const shots = preview?.Shots || [];
    const tieIn = shots.find(s => s.IsTieIn);
    const hasTieInAzimuths = tieIn
        && tieIn.DerivedAzimuth !== null && tieIn.DerivedAzimuth !== undefined;

    // How far the tie-in shot actually moves — the only figure here that can be
    // checked against the table. Deliberately not the derivation's Offset, which
    // is measured against the de-stepped heading and so reads as wrong: a survey
    // whose tie-in shows 27.98 being anchored to 28.5 has an offset of +3.97,
    // because the offset also has to undo the alignment drift that had already
    // accumulated by the tie-in.
    const tieInMove = hasTieInAzimuths ? wrap180(tieIn.DerivedAzimuth - tieIn.CurrentAzimuth) : 0;

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg">
            <ModalHeader toggle={onClose}>Azimuth datum</ModalHeader>
            <ModalBody>
                {error && <div className="alert alert-danger" role="alert">{error}</div>}

                <div className="form-check">
                    <input className="form-check-input" type="radio" id="azimuthModeNorthseek"
                        checked={mode === 'NORTHSEEK'} onChange={() => setMode('NORTHSEEK')} />
                    <label className="form-check-label" htmlFor="azimuthModeNorthseek">
                        <strong>Northseek</strong> — use the tool's own heading, exactly as recorded.
                        Each coarse alignment is treated as a successive improvement in its estimate
                        of north.
                    </label>
                </div>
                <div className="form-check mt-2">
                    <input className="form-check-input" type="radio" id="azimuthModeReference"
                        checked={mode === 'REFERENCE'} onChange={() => setMode('REFERENCE')} />
                    <label className="form-check-label" htmlFor="azimuthModeReference">
                        <strong>Reference</strong> — remove the step each alignment puts in the
                        heading, then anchor the survey to an azimuth you declare.
                    </label>
                </div>

                {mode === 'REFERENCE' && (
                    <div className="row mt-3">
                        <div className="col-sm-4">
                            <label className="form-label" htmlFor="referenceAzimuthInput">
                                Reference azimuth (°)
                            </label>
                            <input className={`form-control${azimuthIsValid ? '' : ' is-invalid'}`}
                                id="referenceAzimuthInput" type="number" step="0.01"
                                value={referenceAzimuth}
                                onChange={e => setReferenceAzimuth(e.target.value)} />
                            <div className="invalid-feedback">Enter an azimuth in degrees.</div>
                        </div>
                        <div className="col-sm-8">
                            <p className="small text-muted mt-4">
                                The tie-in shot will be set to read exactly this. If the survey has a
                                drift check the earliest one is the tie-in; otherwise the earliest shot
                                of the run is used.
                            </p>
                        </div>
                    </div>
                )}

                {loading && <p className="mt-3">Working out what this would do...</p>}

                {preview && !loading && (
                    <div className="mt-3">
                        <table className="table table-sm">
                            <tbody>
                                <tr>
                                    <th scope="row" style={{ width: '14rem' }}>Tie-in shot</th>
                                    <td>
                                        {tieIn
                                            ? <>
                                                {tieIn.Depth.toFixed(2)} m
                                                {tieIn.Timestamp && <> at {new Date(tieIn.Timestamp).toLocaleTimeString()}</>}
                                                {preview.TieInIsDriftCheck
                                                    ? <span className="badge bg-success ms-2">drift check</span>
                                                    : <span className="badge bg-warning text-dark ms-2">no drift check — earliest shot</span>}
                                            </>
                                            : <span className="text-muted">none</span>}
                                    </td>
                                </tr>
                                {hasTieInAzimuths && (
                                    <tr>
                                        <th scope="row">Tie-in azimuth</th>
                                        <td>
                                            <strong>{tieIn.CurrentAzimuth.toFixed(2)}°</strong>
                                            {' → '}
                                            <strong>{tieIn.DerivedAzimuth.toFixed(2)}°</strong>
                                            <span className="text-muted"> ({formatOffset(tieInMove)})</span>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {preview.UnmatchedShots > 0 && (
                            <div className="alert alert-warning" role="alert">
                                {preview.UnmatchedShots} shot{preview.UnmatchedShots === 1 ? ' was' : 's were'} recorded
                                after the last orientation record and will be left unchanged.
                            </div>
                        )}

                        {mode === 'REFERENCE' && Math.abs(tieInMove) > 20 && (
                            <div className="alert alert-warning" role="alert">
                                This moves the tie-in shot by {formatOffset(tieInMove)}. That is
                                expected on older surveys — a run's first shot often lands inside the
                                tool's initial alignment, and some runs genuinely start on a different
                                heading. It cannot affect the survey's internal consistency, but check
                                the tie-in above is the shot you meant.
                            </div>
                        )}
                        {mode === 'REFERENCE' && preview.AlignmentEventCount === 0 && (
                            <div className="alert alert-info" role="alert">
                                This survey records no coarse-alignment counter, so there are no steps
                                to remove — Reference mode will only re-anchor it to your azimuth.
                            </div>
                        )}
                    </div>
                )}

                <p className="small text-muted mt-3 mb-0">
                    The raw orientation records are never modified, so you can switch back at any
                    time and the original azimuths return exactly.
                </p>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button color="primary" disabled={!changed || !azimuthIsValid || saving || loading}
                    onClick={apply}>
                    {saving ? 'Saving...' : 'Apply'}
                </Button>
            </ModalFooter>
        </Modal>
    );
};
